import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'
import { Grid, TextField } from '@mui/material'

import {
    createMembershipSchema,
    CreateMembershipType,
    defaultMembership,
} from '@/api/mem/memberMembership'
import {
    memberMembershipsQueryOptions,
    useCreateMembershipMutation,
    useDeleteMembershipMutation,
    useUpdateMembershipMutation,
} from '@/api/mem/memberMembershipQueryOptions'
import { MembershipType } from '@/api/memberships'
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions'
import CreateDetailAccordion, {
    useCreateDetail,
} from '@/components/detail/CreateDetailRecord'
import EditGrid, { useEditGrid } from '@/components/detail/EditGrid'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents'
import useResponsive from '@/components/hooks/useResponsive'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import SelectOrganization from '@/components/select/SelectOrganization'
import { getOrganization } from '@/store/auth/authSlice'
import { getCurrentMember } from '@/store/nav/navSlice'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'
import i18next, { t } from 'i18next'
import { Controller } from 'react-hook-form'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/memberships',
)({
    component: MembershipsComponent,
})

function MembershipsComponent() {

    const currentMember = useSelector(getCurrentMember)

    const params = Route.useParams()
    const membershipsQuery = useSuspenseQuery(
        memberMembershipsQueryOptions(params.memberUuid),
    )
    const memberships = membershipsQuery.data

    const permissionsQuery = useSuspenseQuery(
        fetchDetailPermissionsQueryOptions('member', params.memberUuid, [
            'membership',
        ]),
    )
    const permissions = permissionsQuery.data

    const createable =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'membership' && p.permissions.some((a) => a === 'create'),
        ).length > 0

    const { onCreated, onUpdated, onDeleted, onError } =
        useDetailCrudEvents<MembershipType>('membership')

    const createMembershipMutation = useCreateMembershipMutation(
        params.memberUuid,
        onCreated,
        onError,
    )
    const updateMembershipMutation = useUpdateMembershipMutation(
        params.memberUuid,
        onUpdated,
        onError,
    )
    const deleteMembershipMutation = useDeleteMembershipMutation(
        params.memberUuid,
        onDeleted,
        onError,
    )

    const basicGridColumns = useBasicGridColumns()
    const sessionOrganization = useSelector(getOrganization)

    const onSubmit = (data: CreateMembershipType) => {
        createMembershipMutation.mutate(data)
    }

    const { xsmall, small } = useResponsive()

    const columns: GridColDef[] = []

    columns.push(basicGridColumns.enhancedScoutstateColumn)
    columns.push(basicGridColumns.organizationNameColumn)
    if (xsmall || small) {
        columns.push(basicGridColumns.validPeriodColumn)
    } else {
        columns.push(basicGridColumns.startDateColumn)
        columns.push(basicGridColumns.endDateColumn)
        columns.push(basicGridColumns.activeColumn)
    }
    columns.push(basicGridColumns.descriptionColumn)

    const createDetail = useCreateDetail<CreateMembershipType>({
        onSubmit,
        resolver: yupResolver<CreateMembershipType, any, any>(createMembershipSchema),
        defaultValues: {
            member: {
                uuid: params.memberUuid,
            },
            organization: {
                uuid: sessionOrganization.uuid,
            },
            scoutstate: 'MEMBER_FULL',
        },
        title: t('action.assign', { entity: t('entity.membership') }),
    })

    const editGrid = useEditGrid<MembershipType>({
        defaultValues: defaultMembership,
        updateMutation: updateMembershipMutation,
        deleteMutation: deleteMembershipMutation,
        records: memberships,
        columns: columns,
        title: i18next.t('entity.membership'),
    })

    if (membershipsQuery.isLoading || membershipsQuery.isFetching) {
        return <LoadProgress />
    }

    if (permissionsQuery.isLoading || permissionsQuery.isFetching) {
        return <LoadProgress />
    }

    return (<>
        {/*JSON.stringify({
        xs: xsmall,
        sm: small,
        md: medium, 
        lg: large,
        xl: xlarge,
      })*/}
        {createable && (
            <CreateDetailAccordion {...createDetail}>
                <Grid container spacing={2}>
                    <input
                        type="hidden"
                        defaultValue={params.memberUuid}
                        {...createDetail.form.register('member.uuid')}
                    />
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <FormControl fullWidth variant="standard">
                            <InputLabel id="scoutstate-select-label">
                                {t('property.membership.scoutstate')}
                            </InputLabel>
                            <Controller
                                name="scoutstate"
                                control={createDetail.form.control}
                                defaultValue="MEMBER_FULL"
                                render={({ field }) => (
                                    <Select labelId="scoutstate-select-label" {...field}>
                                        <MenuItem key="MEMBER_FULL" value="MEMBER_FULL">
                                            {t('enum.scoutstate.MEMBER_FULL')}
                                        </MenuItem>
                                        <MenuItem key="MEMBER_ADD" value="MEMBER_ADD">
                                            {t('enum.scoutstate.MEMBER_ADD')}
                                        </MenuItem>
                                        <MenuItem key="CONTACT" value="CONTACT">
                                            {t('enum.scoutstate.CONTACT')}
                                        </MenuItem>
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    {(sessionOrganization.organizationLevel === 'DIVISION' ||
                        sessionOrganization.organizationLevel === 'FEDERATION') && (
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 4
                                }}>
                                <SelectOrganization control={createDetail.form.control} name="organization.uuid" />
                            </Grid>
                        )}
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 2
                        }}>
                        <ScoregDatePicker
                            name="startDate"
                            defaultValue={dayjs(new Date())}
                            control={createDetail.form.control}
                            label={t('property.general.startDate')}
                            maxDate={dayjs(createDetail.form.getValues('endDate'))}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 2
                        }}>
                        <ScoregDatePicker
                            name="endDate"
                            defaultValue={undefined}
                            control={createDetail.form.control}
                            label={t('property.general.endDate')}
                            minDate={dayjs(createDetail.form.getValues('startDate'))}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <TextField
                            {...createDetail.form.register('description')}
                            label={t('property.general.description')}
                            multiline
                            rows={2} />
                    </Grid>
                </Grid>
            </CreateDetailAccordion>
        )}
        <EditGrid {...editGrid} initialVisibilityModel={{
            "valid_period": false,
        }} detailLink="/membership/$uuid" getScopeName={() => { return currentMember.firstname + "_" + currentMember.lastname }} />
    </>);
}
